import { useId, useState } from "react";
import { CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Calendar, ChoiceChips, cn, Text } from "@athena/ui";
import { formatNumericDay, type FollowUpChoice } from "../helpers/client";

interface FollowUpPickerProps {
    /** The quick picks to offer, in order. "custom" adds a date field below them. */
    choices: FollowUpChoice[];
    choice: FollowUpChoice;
    onChoiceChange: (choice: FollowUpChoice) => void;
    customDate: Date | undefined;
    onCustomDateChange: (date: Date | undefined) => void;
    today: Date;
    labelledBy: string;
    /**
     * Which way the calendar opens from the date field. "above" where the field sits at the foot of
     * a dialog, as in the log-contact dialog; "below" where it sits near the top.
     */
    calendarSide?: "above" | "below";
}

/** "S M T W T F S", as in the mockups. */
const WEEKDAY_FORMAT = new Intl.DateTimeFormat("en-US", { weekday: "narrow" });
/** "Jul 2026". */
const MONTH_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });

/** The mockups' calendar on the shared `Calendar`: a left-aligned month, round arrows, roomier days. */
const CALENDAR_CLASS_NAMES = {
    months: "relative flex flex-col",
    month: "flex w-full flex-col gap-3",
    month_caption: "flex h-9 items-center px-1",
    caption_label: "font-display text-text-heading text-base font-semibold",
    nav: "absolute top-0 right-0 flex items-center gap-2",
    button_previous:
        "border-border bg-card text-text-body hover:bg-accent inline-flex h-9 w-9 items-center justify-center rounded-full border aria-disabled:opacity-40",
    button_next:
        "border-border bg-card text-text-body hover:bg-accent inline-flex h-9 w-9 items-center justify-center rounded-full border aria-disabled:opacity-40",
    weekdays: "flex",
    weekday: "text-text-caption flex-1 font-mono text-xs font-normal uppercase select-none",
    week: "mt-1 flex w-full",
    today: "[&_button]:border-border [&_button]:border",
    outside: "text-text-caption opacity-60",
    disabled: "text-text-caption opacity-40",
} as const;

/**
 * "When is the next contact" — shared by logging a contact and scheduling one on its own.
 *
 * The custom date is a field that shows the picked day and opens a calendar over the form. Over the
 * form, not in a popover: both callers sit in a modal dialog, and a popover's calendar is portalled
 * outside it, where the dialog's focus trap pulls focus straight back and the popover closes. So it
 * stays in the dialog and floats by absolute position instead.
 */
export function FollowUpPicker({
    choices,
    choice,
    onChoiceChange,
    customDate,
    onCustomDateChange,
    today,
    labelledBy,
    calendarSide = "below",
}: FollowUpPickerProps) {
    const { t } = useTranslation();
    const ids = useId();
    const [
        calendarOpen,
        setCalendarOpen,
    ] = useState<boolean>(false);

    const changeChoice = (value: FollowUpChoice) => {
        onChoiceChange(value);
        // The calendar opens from the date field only; switching choices always starts it closed.
        setCalendarOpen(false);
    };

    const pick = (date: Date | undefined) => {
        onCustomDateChange(date);
        setCalendarOpen(false);
    };

    const dateText = customDate ? formatNumericDay(customDate) : t("admin.alumni.followUpChoice.datePlaceholder");

    return (
        <div className="flex flex-col gap-3">
            <ChoiceChips
                aria-labelledby={labelledBy}
                value={choice}
                onValueChange={(value) => changeChoice(value as FollowUpChoice)}
                options={choices.map((value) => ({ value, label: t(`admin.alumni.followUpChoice.${value}`) }))}
            />
            {choice === "custom" ? (
                <div className="flex flex-col gap-2">
                    <Text as="span" size="sm" weight="semibold" aria-hidden>
                        {t("admin.alumni.followUpChoice.dateLabel")}
                    </Text>
                    <div className="relative w-full max-w-60">
                        <button
                            type="button"
                            aria-label={`${t("admin.alumni.followUpChoice.dateLabel")}: ${dateText}`}
                            aria-expanded={calendarOpen}
                            aria-controls={`${ids}-calendar`}
                            className={cn(
                                "border-border bg-card flex h-11 w-full items-center justify-between rounded-full border px-5 text-base transition-colors",
                                "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                                calendarOpen && "border-secondary",
                            )}
                            onClick={() => setCalendarOpen((open) => !open)}
                        >
                            <span className={customDate ? "text-text-heading" : "text-text-caption"}>{dateText}</span>
                            <CalendarDays className="text-text-heading h-4 w-4" aria-hidden />
                        </button>
                        {calendarOpen ? (
                            <div
                                id={`${ids}-calendar`}
                                className={cn(
                                    "border-border bg-card absolute left-0 z-20 w-max rounded-2xl border p-4 shadow-lg",
                                    calendarSide === "above" ? "bottom-full mb-2" : "top-full mt-2",
                                )}
                            >
                                <Calendar
                                    className="bg-transparent p-0 [--cell-size:2.5rem]"
                                    classNames={CALENDAR_CLASS_NAMES}
                                    formatters={{
                                        formatCaption: (month) => MONTH_FORMAT.format(month),
                                        formatWeekdayName: (day) => WEEKDAY_FORMAT.format(day),
                                    }}
                                    mode="single"
                                    selected={customDate}
                                    defaultMonth={customDate ?? today}
                                    onSelect={pick}
                                    disabled={{ before: today }}
                                />
                                <div className="border-border mt-3 flex items-center justify-between border-t pt-3">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-text-muted w-auto tracking-normal normal-case"
                                        onClick={() => pick(undefined)}
                                    >
                                        {t("admin.alumni.followUpChoice.clear")}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-primary w-auto tracking-normal normal-case"
                                        onClick={() => pick(today)}
                                    >
                                        {t("admin.alumni.followUpChoice.today")}
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
